import { useDevisActif } from '../hooks/useDevisActif';
import { DepartsChoix, titreDeparts } from '../components/DepartsChoix';
import React, { useEffect, useState, useCallback } from 'react';
import { REPAS, codeRepas, formuleRepasLabel, etapesRepas } from '../utils/repas';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import InfoModal from '../components/InfoModal';
import ExcursionsPublicSection from '../components/ExcursionsPublicSection';
import SpaBloc from '../components/SpaBloc';
import ALaCarteBloc from '../components/ALaCarteBloc';
import VideosYoutube from '../components/VideosYoutube';
import TripMobileBar from '../components/TripMobileBar';
import ShareButton from '../components/ShareButton';
import { usePagesTextes, TexteRiche } from '../hooks/useFormTextes';

const AttenteIntro = () => { const TP = usePagesTextes(); return <TexteRiche texte={TP('attente_intro')} />; };

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Iter 156f (Kamel — Lloret été : liste d'attente interne par semaine)
// WaitlistForm accepte optionnellement weekKey/weekLabel/transportLabel pour
// les voyages multi-semaines (Lloret). Le voyage_key envoyé au backend
// distingue alors chaque combinaison (semaine × transport) afin que Kamel
// puisse filtrer dans l'onglet admin "Liste d'attente".
const WaitlistForm = ({ tripTitle, tripId, startDate, weekKey, weekLabel, transportLabel, onSubmitted }) => {
  const [form, setForm] = useState({ nom: '', prenom: '', nbPersonnes: 1, telephone: '', email: '' });
  const [submitted, setSubmitted] = useState(false);
  const [dup, setDup] = useState(null);
  const [error, setError] = useState('');

  const isWeekly = Boolean(weekKey);
  const headline = isWeekly
    ? `${tripTitle} — ${weekLabel}${transportLabel ? ` · ${transportLabel}` : ''}`
    : tripTitle;
  const voyageKey = isWeekly
    ? `${tripTitle} | ${weekLabel}${transportLabel ? ` | ${transportLabel}` : ''}`
    : tripTitle;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.prenom.trim()) {
      setError('Nom et prénom sont obligatoires.');
      return;
    }
    if (!form.email.trim() && !form.telephone.trim()) {
      setError('Merci de renseigner au moins un moyen de contact (email ou téléphone).');
      return;
    }
    try {
      const r = await axios.post(`${API}/waitlist`, {
        voyage_key: voyageKey,
        destination: headline,
        nom: form.nom.toUpperCase(),
        prenom: form.prenom,
        nbPersonnes: parseInt(form.nbPersonnes) || 1,
        telephone: form.telephone,
        email: form.email,
        note: isWeekly ? `Semaine ${weekKey}${transportLabel ? ` (${transportLabel})` : ''}` : '',
        source: 'site',
      });
      if (r.data?.duplicate) setDup(r.data.depuis || 'déjà');
      setSubmitted(true);
      if (onSubmitted) onSubmitted();
    } catch {
      setError('Erreur lors de l\'envoi. Réessayez ou contactez-nous par WhatsApp.');
    }
  };

  if (submitted) {
    return (
      <div className="bg-green-50 border-2 border-green-300 rounded-2xl p-6 text-center" data-testid="waitlist-success">
        {dup ? (
          <>
            <p className="text-green-700 font-bold text-lg">Vous êtes déjà en liste d'attente ✅</p>
            <p className="text-sm text-gray-600 mt-2">
              Nous avons bien retrouvé votre demande{dup !== 'déjà' ? ` du ${dup.split('-').reverse().join('/')}` : ''} pour <strong>{headline}</strong> — votre position d'origine est conservée, inutile de vous réinscrire. Nous vous recontacterons dès qu'une place se libère.
            </p>
          </>
        ) : (
          <>
            <p className="text-green-700 font-bold text-lg">Demande enregistrée !</p>
            <p className="text-sm text-gray-600 mt-2">
              Nous vous recontacterons dès qu'une place se libère pour <strong>{headline}</strong>.
            </p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2a4f7a] rounded-2xl p-6 text-white" data-testid="waitlist-form">
      <h4 className="font-bold text-lg mb-1 text-center">S'inscrire en liste d'attente</h4>
      {isWeekly && (
        <p className="text-sm text-white font-semibold text-center mb-2" data-testid="waitlist-week-headline">
          📍 {tripTitle}<br/>🗓️ {weekLabel}{transportLabel ? ` · 🚌 ${transportLabel}` : ''}
        </p>
      )}
      <p className="text-sm text-white/80 text-center mb-4"><AttenteIntro /></p>
      {error && <p className="text-red-300 text-xs text-center mb-2">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <input type="text" placeholder="NOM *" value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value.toUpperCase() })}
            className="px-3 py-2 rounded-lg text-sm text-gray-800 bg-white" required data-testid="waitlist-nom" />
          <input type="text" placeholder="Prénom *" value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm text-gray-800 bg-white" required data-testid="waitlist-prenom" />
        </div>
        <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm text-gray-800 bg-white" data-testid="waitlist-email" />
        <div className="grid grid-cols-2 gap-2">
          <input type="tel" placeholder="Téléphone" value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm text-gray-800 bg-white" data-testid="waitlist-tel" />
          <input type="number" min="1" max="10" value={form.nbPersonnes} onChange={e => setForm({ ...form, nbPersonnes: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm text-gray-800 bg-white" data-testid="waitlist-nb" />
        </div>
        <p className="text-[11px] text-white/70 text-center">Indiquez au moins un moyen de contact (email ou téléphone). Nous vous préviendrons dès qu'une place se libère.</p>
        <button type="submit" className="w-full bg-white text-[#1E3A5F] px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition text-center" data-testid="waitlist-submit">
          S'inscrire en liste d'attente
        </button>
      </form>
    </div>
  );
};

// Iter 156f — Modale qui héberge WaitlistForm pour une semaine précise.
// Cliquer hors de la modale ou sur la croix la ferme. Bloquant léger overlay.
const WaitlistWeekModal = ({ open, onClose, trip, weekKey, weekLabel, transportLabel }) => {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid="waitlist-week-modal"
    >
      <div
        className="relative w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute -top-2 -right-2 z-10 w-9 h-9 rounded-full bg-white text-[#1E3A5F] shadow-lg flex items-center justify-center font-bold text-xl hover:bg-gray-100"
          data-testid="waitlist-week-modal-close"
        >
          ×
        </button>
        <WaitlistForm
          tripTitle={trip.title}
          tripId={trip.id}
          startDate={trip.start_date}
          weekKey={weekKey}
          weekLabel={weekLabel}
          transportLabel={transportLabel}
        />
      </div>
    </div>
  );
};

// Iter 149 — Solo Matching : voyageurs solo qui cherchent un·e partenaire de chambre
// Iter 149c — Affichage uniquement si voyage avec hébergement (>= 1 nuit)
const SoloMatchingForm = ({ tripTitle, tripId }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(null); // {total, femmes, hommes}
  const [form, setForm] = useState({
    prenom: '', nom: '', email: '', telephone: '',
    sexe: 'F', date_naissance: '', note: '',
    consent_rgpd: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Charge le compteur public anonymisé pour ce voyage
  useEffect(() => {
    if (!tripId) return;
    axios.get(`${API}/solo/count`, { params: { trip_id: tripId } })
      .then(r => setCount(r.data))
      .catch(() => setCount(null));
  }, [tripId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.consent_rgpd) {
      setError('Merci de cocher la case de consentement RGPD pour continuer.');
      return;
    }
    if (!form.email.trim() || !form.telephone.trim()) {
      setError('Email ET téléphone sont nécessaires pour pouvoir vous mettre en relation.');
      return;
    }
    if (!form.date_naissance) {
      setError('Merci d\'indiquer votre date de naissance.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/solo/register`, {
        trip_id: tripId,
        trip_title: tripTitle,
        prenom: form.prenom,
        nom: form.nom.toUpperCase(),
        email: form.email,
        telephone: form.telephone,
        sexe: form.sexe,
        date_naissance: form.date_naissance,
        note: form.note,
        consent_rgpd: form.consent_rgpd,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi. Réessayez ou contactez-nous.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="bg-blue-50 border-2 border-[#1E3A5F] rounded-2xl p-6 text-center" data-testid="solo-success">
        <p className="text-2xl mb-2">🤝</p>
        <p className="text-[#1E3A5F] font-bold text-lg">Vous êtes inscrit·e en liste de partage !</p>
        <p className="text-sm text-gray-600 mt-2">Nous vous contacterons dès qu'un·e voyageur·euse compatible s'inscrit pour <strong>{tripTitle}</strong>.</p>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="bg-gradient-to-br from-[#e0f2fe] to-[#bae6fd] border-2 border-[#5BC5D4] rounded-2xl p-5 text-center" data-testid="solo-cta">
        <p className="text-3xl mb-2">🤝</p>
        <h4 className="font-bold text-lg text-[#1E3A5F] mb-1">Vous partez seul·e ?</h4>
        <p className="text-sm text-gray-700 mb-3">
          Trouvez un·e compagnon·ne de chambre pour ce voyage et évitez le supplément chambre individuelle.
        </p>
        {count && count.total > 0 && (
          <p className="text-xs text-[#1E3A5F] font-semibold mb-3" data-testid="solo-count">
            👥 {count.total} personne{count.total > 1 ? 's' : ''} cherche{count.total > 1 ? 'nt' : ''} un·e partenaire
            {count.femmes > 0 || count.hommes > 0 ? ` (${count.femmes} femme${count.femmes > 1 ? 's' : ''}, ${count.hommes} homme${count.hommes > 1 ? 's' : ''})` : ''}
          </p>
        )}
        <button
          onClick={() => setOpen(true)}
          className="bg-[#1E3A5F] hover:bg-[#2d5a8f] text-white px-6 py-3 rounded-xl font-bold transition shadow-lg hover:shadow-xl"
          data-testid="solo-open-form-btn"
        >
          Je m'inscris en liste de partage
        </button>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#e0f2fe] to-[#bae6fd] border-2 border-[#5BC5D4] rounded-2xl p-6" data-testid="solo-form">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-bold text-lg text-[#1E3A5F]">🤝 Liste de partage de chambre</h4>
        <button onClick={() => setOpen(false)} className="text-gray-500 hover:text-red-600 text-xl" data-testid="solo-close-btn">✕</button>
      </div>
      <p className="text-xs text-gray-700 mb-4">
        Vous voyagez seul·e ? Inscrivez-vous ici. Nous vous mettrons en relation avec une autre personne solo
        qui cherche aussi un·e partenaire pour le même voyage. <strong>Vos coordonnées ne sont visibles que par RAAV.</strong>
      </p>
      {error && <p className="text-red-700 text-xs text-center mb-2 bg-red-50 border border-red-200 rounded p-2" data-testid="solo-error">{error}</p>}
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-2 gap-2">
          <input type="text" placeholder="Prénom *" value={form.prenom}
            onChange={e => setForm({ ...form, prenom: e.target.value })}
            className="px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-prenom" />
          <input type="text" placeholder="NOM *" value={form.nom}
            onChange={e => setForm({ ...form, nom: e.target.value.toUpperCase() })}
            className="px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-nom" />
        </div>
        <input type="email" placeholder="Email *" value={form.email}
          onChange={e => setForm({ ...form, email: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-email" />
        <input type="tel" placeholder="Téléphone *" value={form.telephone}
          onChange={e => setForm({ ...form, telephone: e.target.value })}
          className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-telephone" />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] text-gray-700 font-semibold uppercase block mb-0.5">Sexe *</label>
            <select value={form.sexe} onChange={e => setForm({ ...form, sexe: e.target.value })}
              className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-sexe">
              <option value="F">Femme</option>
              <option value="H">Homme</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] text-gray-700 font-semibold uppercase block mb-0.5">Date de naissance *</label>
            <input type="date" value={form.date_naissance}
              onChange={e => setForm({ ...form, date_naissance: e.target.value })}
              className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4]" required data-testid="solo-ddn" />
          </div>
        </div>
        <textarea placeholder="Note libre (non-fumeur·euse, lève-tôt, demandes particulières…) — facultatif"
          value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
          rows={2} maxLength={280}
          className="w-full px-3 py-2 rounded-lg text-sm bg-white border border-[#5BC5D4] resize-none" data-testid="solo-note" />
        <label className="flex items-start gap-2 text-xs text-gray-700 cursor-pointer">
          <input type="checkbox" checked={form.consent_rgpd}
            onChange={e => setForm({ ...form, consent_rgpd: e.target.checked })}
            className="mt-0.5" required data-testid="solo-consent" />
          <span>
            J'accepte que RAAV utilise mes coordonnées exclusivement pour me proposer une mise en relation
            avec un·e autre voyageur·euse solo de ce voyage. Mes coordonnées ne seront partagées avec personne
            sans mon accord explicite ultérieur.
          </span>
        </label>
        <button type="submit" disabled={submitting}
          className={`w-full px-6 py-3 rounded-xl font-bold transition text-center ${submitting ? 'bg-gray-400 text-white cursor-wait' : 'bg-[#1E3A5F] hover:bg-[#2d5a8f] text-white shadow-lg hover:shadow-xl'}`}
          data-testid="solo-submit">
          {submitting ? '⏳ Envoi…' : '🤝 Je m\'inscris en liste de partage'}
        </button>
      </form>
    </div>
  );
};

const Lightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div
      data-testid="lightbox-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      <button
        data-testid="lightbox-close-btn"
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white z-50 bg-black/40 rounded-full w-10 h-10 flex items-center justify-center transition-colors"
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
      </button>

      <button
        data-testid="lightbox-prev-btn"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 text-white/80 hover:text-white z-50 bg-black/40 rounded-full w-12 h-12 flex items-center justify-center transition-colors"
      >
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
      </button>

      <button
        data-testid="lightbox-next-btn"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 text-white/80 hover:text-white z-50 bg-black/40 rounded-full w-12 h-12 flex items-center justify-center transition-colors"
      >
        <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
      </button>

      <div className="max-w-5xl max-h-[85vh] px-4" onClick={(e) => e.stopPropagation()}>
        <img
          data-testid="lightbox-image"
          src={images[currentIndex]}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-full max-h-[80vh] object-contain rounded-lg"
        />
        <div className="text-center text-white/70 mt-3 text-sm">
          {currentIndex + 1} / {images.length}
        </div>
      </div>
    </div>
  );
};

const estCourtSejour = (t) => /printemps|4 jours|week-end/i.test(t?.title || '') || !!(t?.duration_days && t.duration_days <= 4);
const motSejour = (t) => estCourtSejour(t) ? 'séjour' : 'semaine';

const TripDetail = () => {
  const TP = usePagesTextes(); // Iter 767 — phrases communes éditables (Textes du site → Pages voyage)
  const devis = useDevisActif();
  const { id } = useParams();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  // Modale info (pdf programme / échéancier / assurance)
  const [infoModal, setInfoModal] = useState({ open: false, type: null, title: '', pdfUrl: null });
  // Iter 156f — Modale liste d'attente par semaine (Lloret été)
  const [waitlistWeekModal, setWaitlistWeekModal] = useState({ open: false, weekKey: '', weekLabel: '', transportLabel: '' });
  const [dispoAuto, setDispoAuto] = useState({});  // Iter 688 — compteur automatique (places restantes, trajets Aller / Retour)
  const openInfo = (type, title, pdfUrl = null) => setInfoModal({ open: true, type, title, pdfUrl });
  const closeInfo = () => setInfoModal({ open: false, type: null, title: '', pdfUrl: null });

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchTrip = async () => {
      try {
        const response = await axios.get(`${API}/trips/${id}`);
        if (response.data?.redirect_to) { window.location.replace(`/voyage/${response.data.redirect_to}`); return; }  // Iter 937 — page regroupée : ancien lien préservé
        setTrip(response.data);
        setLoading(false);
        axios.get(`${API}/disponibilites`).then(r => setDispoAuto(r.data || {})).catch(() => {});
      } catch (error) {
        setLoading(false);
      }
    };
    fetchTrip();
    // Iter 235 (Kamel) — Compteur de vues (fire-and-forget).
    axios.post(`${API}/trips/${id}/vue`).catch(() => {});
  }, [id]);

  const allImages = trip ? [trip.image_url, ...(trip.gallery_images || [])].filter(Boolean) : [];

  const openLightbox = useCallback((index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const prevImage = useCallback(() => setLightboxIndex(i => (i - 1 + allImages.length) % allImages.length), [allImages.length]);
  const nextImage = useCallback(() => setLightboxIndex(i => (i + 1) % allImages.length), [allImages.length]);

  const getCountryFlag = (code) => {
    if (!code) return null;
    const lower = code.toLowerCase();
    return (
      <img
        src={`https://flagcdn.com/w80/${lower}.png`}
        srcSet={`https://flagcdn.com/w160/${lower}.png 2x`}
        alt={code}
        className="inline-block rounded shadow-sm"
        style={{ width: '48px', height: '32px', objectFit: 'cover' }}
      />
    );
  };

  // Iter 937 — page commune à plusieurs départs (trip.departs) : jamais « du 6 au 18 mai », prix = celui des déclinaisons
  const multiDeparts = !!trip?.multi_departs && (trip?.departs || []).length >= 1;
  // Iter 938 — séjours fusionnés (car en option) : une ligne par séjour réellement proposé (weekly_availability), jamais une seule
  // ligne « 6 au 9 mai » quand la page propose deux séjours au même prix
  const weeksRows = (() => {
    const wp = trip?.weekly_pricing || [];
    const fusionRow = wp.find(w => w.fusion);
    const keys = Object.keys(trip?.weekly_availability || {}).filter(k => !/-(sans|avec)$/.test(k));
    if (!fusionRow || wp.length !== 1 || keys.length < 2) return wp;
    const labelDe = (k) => (trip.departs || []).find(d => (d.fd_id || '').endsWith(`-${k}`))?.label || k;
    return keys.map(k => ({ ...fusionRow, key: k, label: labelDe(k) }));
  })();
  const prixAffiche = multiDeparts && trip.departs.some(d => d.price != null) ? Math.min(...trip.departs.filter(d => d.price != null).map(d => d.price)) : trip?.price_from;
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(date);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!trip) {
    return (
      <div className="section text-center">
        <h2 className="text-2xl font-bold mb-4">Voyage non trouvé</h2>
        <Link to="/voyages" className="btn-primary">Retour aux voyages</Link>
      </div>
    );
  }

  // Iter 333 — Compteur AUTOMATIQUE (dispo_auto) prioritaire, sinon repli sur l'ancien champ manuel
  const dispoStatut = trip.dispo_auto
    ? trip.dispo_auto.statut
    : (trip.sold_out || trip.available_spots === 0)
      ? 'complet'
      : trip.available_spots <= 10 ? 'moins_10' : 'disponible';
  // Iter 149b — Disponibilité du voyage (mêmes critères que le ternaire sidebar)
  const isTripAvailable = (
    trip.date_options
      ? trip.date_options.some((d) => d.status === 'disponible')
      : (trip.weekly_availability && Object.keys(trip.weekly_availability).length) // Iter 855 — {} vide (Provence) = pas de semaines
        ? Object.values(trip.weekly_availability).some((v) => v === -1 || v > 0)
        : dispoStatut !== 'complet'
  );
  const isCarTrip = ['autocar', 'car', 'bus'].includes((trip.transport || '').toLowerCase()) || !!trip.transport_option || !!trip.car_schedule;
  // Iter 759 (Kamel) — l'exemple « famille de 4, deux semaines : X € d'économie » parle du CAR (un trajet par semaine) → il vit dans le bloc Transport, pas dans le forfait hôtel
  const legPrice = trip.transport_option?.price_per_leg || trip.weekly_pricing?.find(w => w.transport_price_per_leg)?.transport_price_per_leg;
  const famille2Semaines = (() => {
    if (!trip.weekly_pricing?.some(w => w.fusion) || !legPrice) return null;
    const weeksCar = trip.weekly_pricing.filter(w => w.transport_price_per_leg);
    const semOf = (k) => (devis.semaines || []).find(sm => (sm.id || '').endsWith(`-${k}`));
    for (let k = 0; k + 1 < weeksCar.length; k++) {
      const a = semOf(weeksCar[k].key), b = semOf(weeksCar[k + 1].key);
      if (a && b && a.end === b.start) return [weeksCar[k], weeksCar[k + 1]];
    }
    return null;
  })();
  // Iter 941 (Kamel) — ramassage possible sur les aires d'autoroute, rappelé dans chaque bloc Transport en car
  const airesInfo = (
    <div className="flex items-start gap-3 rounded-xl border border-[#5BC5D4]/40 bg-[#5BC5D4]/10 px-4 py-3" data-testid="aires-autoroute-info">
      <span className="text-xl leading-none" aria-hidden>🛣️</span>
      <p className="text-xs text-gray-700 leading-relaxed">
        <strong className="text-[#1E3A5F]">Vous habitez loin de nos villes de départ ?</strong> Nous pouvons vous récupérer sur une aire d'autoroute
        située sur notre itinéraire (A47, A46, puis A43, A7 ou A9 selon la destination). Choisissez « Aire d'autoroute » comme lieu de départ
        à l'inscription : un horaire de rendez-vous vous sera communiqué.
      </p>
    </div>
  );
  // Iter 759 — MonBus vit dans le bloc Transport (plus dans la colonne de droite)
  const monBusPromo = (
    <div className="bg-[#1E3A5F] rounded-2xl p-5 text-center" data-testid="monbus-promo-trip">
      <div className="bg-white rounded-xl px-4 py-2 inline-block">
        <img src="/monbus-logo.png" alt="MonBus" className="h-7 w-auto" />
      </div>
      <p className="text-white text-sm font-bold mt-3">« Je choisis ma place, je voyage serein »</p>
      <p className="text-gray-300 text-xs mt-1 leading-relaxed">
        Gratuit : dès votre règlement, choisissez vous-même vos places dans le car depuis votre espace adhérent.
        <strong> Première arrivée, première servie !</strong>
      </p>
    </div>
  );

  return (
    <div className="trip-detail-page" data-testid="trip-detail-page">
      {/* Hero Image */}
      <div className="relative h-96 overflow-hidden cursor-pointer" onClick={() => openLightbox(0)}>
        <img
          src={trip.image_url}
          alt={trip.destination} style={{ objectPosition: trip.image_focus ? `${trip.image_focus.x}% ${trip.image_focus.y}%` : 'center' }}
          className="w-full h-full object-cover"
          data-testid="trip-hero-image"
        />
        <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="text-center text-white">
            <div className="inline-block px-6 py-3 bg-blue-600 bg-opacity-80 rounded-full text-3xl font-bold mb-4">
              {getCountryFlag(trip.country_code)}
            </div>
            <h1 className="text-5xl font-bold mb-2" data-testid="trip-title">{trip.title}
              {trip.start_date && String(new Date().getFullYear()) !== trip.start_date.slice(0, 4) && (
                <span className="ml-3 align-middle inline-block bg-amber-400 text-amber-950 text-base px-3 py-1 rounded-full font-extrabold" data-testid="trip-year-badge">Édition {trip.start_date.slice(0, 4)}</span>
              )}
            </h1>
            <p className="text-2xl">{trip.destination}</p>
          </div>
        </div>
        <div className="absolute bottom-4 left-4 z-10">
          <ShareButton trip={trip} />
        </div>
        {allImages.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm flex items-center gap-2 backdrop-blur-sm">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
            {allImages.length} photos
          </div>
        )}
      </div>

      {/* Iter 149b — Bannière "VOYAGE COMPLET" proéminente sous le hero (Kamel)
          Visible AVANT le contenu, scroll automatique vers le formulaire de liste d'attente.
          Iter 156f — Masquée pour les voyages multi-semaines (Lloret été) :
          chaque semaine a son propre badge "⏰ Liste d'attente" dans le tableau. */}
      {!isTripAvailable && !(trip.weekly_pricing && trip.weekly_pricing.length > 0) && (
        <div
          className="bg-gradient-to-r from-red-600 via-red-500 to-rose-600 text-white shadow-lg"
          data-testid="hero-waitlist-banner"
        >
          <div className="container-custom py-4 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
            <div className="flex items-center gap-3">
              <span className="text-3xl animate-pulse">🚨</span>
              <div className="text-center sm:text-left">
                <p className="font-bold text-xl leading-tight">Voyage actuellement complet</p>
                <p className="text-sm text-white/90">Mais ne partez pas tout de suite — des places se libèrent souvent !</p>
              </div>
            </div>
            <button
              onClick={() => {
                const target = document.getElementById('waitlist-anchor');
                if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              data-testid="hero-waitlist-cta"
              className="bg-white text-red-700 hover:bg-red-50 font-bold px-6 py-3 rounded-xl shadow-md hover:shadow-lg transition transform hover:scale-105 whitespace-nowrap"
            >
              👇 Inscrivez-vous en liste d'attente
            </button>
          </div>
        </div>
      )}

      {/* Iter 156g (Kamel) — Tableau "Disponibilités par semaine" MIS EN VALEUR EN HAUT
          de la page (juste sous le hero image) pour consultation rapide des places
          dispo et accès direct à la liste d'attente. Spécifique aux voyages
          multi-semaines (Lloret été : 9 semaines × 2 formules transport). */}
      {/* Iter 749 (Kamel) — accès direct au formulaire d'inscription en haut de page */}
      {/* Iter 759 — sur grand écran, le bouton vit dans la colonne de droite (toujours visible) : on ne le répète pas ici */}
      <section className="container-custom pt-4 pb-1 lg:hidden" data-testid="top-inscription-cta">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{(trip.weekly_pricing || multiDeparts) && !trip.prix_identique ? 'À partir de' : 'Prix de base'}</div>
            <div className="text-2xl font-black text-[#5BC5D4] leading-none" data-testid="top-trip-price">{prixAffiche}&euro;</div>
            <div className="text-[11px] text-gray-600 mt-1" data-testid="top-trip-dates">{multiDeparts ? `${titreDeparts(trip)} : ${trip.departs.map(d => d.label).join(' · ')}` : `${formatDate(trip.start_date)}${trip.start_date !== trip.end_date ? ` → ${formatDate(trip.end_date)}` : ''}`}</div>
          </div>
          <Link to="/inscription" onClick={() => window.scrollTo(0, 0)} data-testid="top-inscription-btn"
            className="inline-flex items-center gap-2 bg-[#C60B1E] hover:bg-[#a30918] text-white font-black px-4 py-3 rounded-full shadow-lg transition text-sm whitespace-nowrap">
            ✍️ S'inscrire
          </Link>
        </div>
      </section>
      {/* Iter 684 — Devis Espagne (réactivé, piloté depuis l'admin) sur les fiches Lloret */}
      {devis.actif && /lloret|espagne/i.test(`${trip.title} ${trip.destination}`) && (
        <section className="container-custom py-2" data-testid="spain-simulator-cta">
          <Link to="/simulateur-espagne" onClick={() => window.scrollTo(0, 0)} data-testid="spain-simulator-btn"
            className="block w-full sm:max-w-md mx-auto bg-[#FFC400] text-[#1E3A5F] px-6 py-3 rounded-xl font-bold hover:bg-[#FFD640] transition-all text-center shadow-md">
            🧮 Calculer votre devis Espagne {devis.annee || ''}
          </Link>
        </section>
      )}
      {trip.weekly_pricing && trip.weekly_pricing.length > 0 && (() => {
        // Iter 691 (Kamel) — semaines fusionnées : une ligne = le séjour ; 3 compteurs (sans transport / car aller / car retour)
        const fusionMode = trip.weekly_pricing.some(w => w.fusion);
        const pfx = /printemps|4 jours/i.test(trip.title || '') ? 'lloret-4j-' : 'lloret-8j-';
        return (
        <div className="bg-gradient-to-br from-[#e0f2fe] via-white to-[#f0f9ff] border-b-2 border-[#79c9ea]/40 py-6 sm:py-8" data-testid="weekly-availability-hero">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto bg-white rounded-2xl shadow-xl border-2 border-[#79c9ea]/50 overflow-hidden">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2a4f7a] px-5 py-4 text-white">
                <h2 className="text-lg sm:text-xl font-bold flex items-center gap-2">
                  <span className="text-2xl">📅</span>
                  Disponibilités par {motSejour(trip)}
                </h2>
                <p className="text-xs sm:text-sm text-white/85 mt-0.5">
                  <TexteRiche texte={TP(fusionMode ? (estCourtSejour(trip) ? 'dispo_intro_fusion_court' : 'dispo_intro_fusion') : 'dispo_intro')} />
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="weekly-availability-hero-table">
                  <thead className="bg-[#79c9ea] text-white">
                    <tr>
                      <th className="px-3 sm:px-4 py-2.5 text-left font-semibold">{estCourtSejour(trip) ? 'Séjour' : 'Semaine'}</th>
                      <th className="px-3 sm:px-4 py-2.5 text-center font-semibold">{fusionMode ? '🏨 Sans transport' : 'Sans transport'}</th>
                      {fusionMode ? (estCourtSejour(trip) ? <th className="px-3 sm:px-4 py-2.5 text-center font-semibold">🚌 Car aller-retour</th>
                        : <><th className="px-3 sm:px-4 py-2.5 text-center font-semibold">🛫 Car aller</th><th className="px-3 sm:px-4 py-2.5 text-center font-semibold">🛬 Car retour</th></>)
                        : <th className="px-3 sm:px-4 py-2.5 text-center font-semibold">Avec transport</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {weeksRows.map((week, i) => {
                      const sansKey = `${week.key}-sans`;
                      const avecKey = `${week.key}-avec`;
                      // Iter 688 — le compteur automatique (capacité − inscrits) prime sur la saisie manuelle ; été avec car : 2 compteurs Aller / Retour
                      const autoFor = (suffix) => dispoAuto[`lloret-8j-${week.key}-${suffix}`] || dispoAuto[`lloret-4j-${week.key}-${suffix}`];
                      const autoSans = autoFor('sans'); const autoAvec = autoFor('avec');
                      // Iter 747 (Kamel) — jamais de nombre de places affiché : Disponible / Moins de 10 places / Moins de 5 places / COMPLET
                      // Iter 761 (Kamel) — mobile : libellés courts pour que le tableau reste lisible (texte complet dès sm)
                      const spotLabel = (v) => v == null ? '—' : v === 0 ? 'COMPLET' : (v !== -1 && v <= 5) ? <><span className="sm:hidden">&lt; 5 pl.</span><span className="hidden sm:inline">Moins de 5 places</span></> : (v !== -1 && v <= 10) ? <><span className="sm:hidden">&lt; 10 pl.</span><span className="hidden sm:inline">Moins de 10 places</span></> : <><span className="sm:hidden">✓</span><span className="hidden sm:inline">✓ Disponible</span></>;
                      const spotColor = (v) => v == null ? 'text-gray-300' : v === 0 ? 'text-red-600' : (v !== -1 && v <= 5) ? 'text-red-500' : (v !== -1 && v <= 10) ? 'text-orange-500' : 'text-green-600';
                      const openWaitlist = (transport) => {
                        const transportLabel = transport === 'avec' ? 'Avec transport' : 'Sans transport';
                        setWaitlistWeekModal({
                          open: true,
                          weekKey: week.fusion ? week.key : (transport === 'avec' ? avecKey : sansKey),
                          weekLabel: week.label,
                          transportLabel,
                        });
                      };
                      if (week.fusion) {
                        const auto = dispoAuto[`${pfx}${week.key}`];
                        const manuel = trip.weekly_availability?.[week.key];
                        const sansV = auto?.sans_transport ? auto.sans_transport.restantes : (auto && !auto.trajets ? auto.restantes : (manuel ?? -1));
                        const legV = (l) => week.transport_price_per_leg == null ? null : (auto?.trajets ? auto.trajets[l].restantes : -1);
                        const cell = (v, leg) => v == null ? <span className="text-gray-300 italic text-xs font-normal"><span className="sm:hidden">—</span><span className="hidden sm:inline">pas de car</span></span>
                          : v === 0 ? <button type="button" onClick={() => openWaitlist(leg ? 'avec' : 'sans')} className="inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2.5 py-1 rounded-full text-[11px] font-bold" data-testid={`hero-waitlist-${week.key}-${leg || 'sans'}`}>⏰ Liste d&apos;attente</button>
                          : <span className={spotColor(v)}>{spotLabel(v)}</span>;
                        return (
                          <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-sky-50 transition`} data-testid={`hero-week-row-${i}`}>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 font-semibold text-[#1E3A5F] text-xs sm:text-sm whitespace-nowrap">{week.label}<span className="block text-[11px] font-normal text-gray-500">{week.price} € / pers.</span></td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold" data-testid={`hero-sans-${week.key}`}>{cell(sansV)}</td>
                            {estCourtSejour(trip) ? (
                              <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold" data-testid={`hero-aller-retour-${week.key}`}>{cell((() => { const a = legV('aller'), r = legV('retour'); if (a == null || r == null) return null; const vals = [a, r].filter(v => v !== -1); return vals.length ? Math.min(...vals) : -1; })(), 'aller')}</td>
                            ) : (<>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold" data-testid={`hero-aller-${week.key}`}>{cell(legV('aller'), 'aller')}</td>
                            <td className="px-2 sm:px-4 py-2 sm:py-3 text-center font-bold" data-testid={`hero-retour-${week.key}`}>{cell(legV('retour'), 'retour')}</td>
                            </>)}
                          </tr>
                        );
                      }
                      const sansSpots = autoSans ? autoSans.restantes : trip.weekly_availability?.[sansKey];
                      const avecSpots = autoAvec ? autoAvec.restantes : trip.weekly_availability?.[avecKey];
                      const trajets = autoAvec?.trajets;
                      const hasTransport = week.transport_price != null;
                      return (
                        <tr key={i} className={`border-t border-gray-100 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-sky-50 transition`} data-testid={`hero-week-row-${i}`}>
                          <td className="px-3 sm:px-4 py-3 font-semibold text-[#1E3A5F]">{week.label}</td>
                          <td className={`px-3 sm:px-4 py-3 text-center font-bold ${spotColor(sansSpots)}`}>
                            {sansSpots === 0 ? (
                              <button type="button" onClick={() => openWaitlist('sans')}
                                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-full text-xs font-bold transition shadow-md hover:shadow-lg hover:scale-105"
                                data-testid={`hero-waitlist-${week.key}-sans`}
                                title={`S'inscrire en liste d'attente pour ${week.label} — SANS transport`}>
                                ⏰ Liste d&apos;attente
                              </button>
                            ) : spotLabel(sansSpots)}
                          </td>
                          <td className={`px-3 sm:px-4 py-3 text-center font-bold ${!hasTransport ? 'text-gray-300 italic' : spotColor(avecSpots)}`}>
                            {!hasTransport ? '—' : trajets ? (
                              <div className="text-xs leading-5" data-testid={`hero-trajets-${week.key}`}>
                                <span className={spotColor(trajets.aller.restantes)}>🛫 Aller : {spotLabel(trajets.aller.restantes)}</span><br/>
                                <span className={spotColor(trajets.retour.restantes)}>🛬 Retour : {spotLabel(trajets.retour.restantes)}</span>
                                {(trajets.aller.restantes <= 0 && trajets.retour.restantes <= 0) && (
                                  <button type="button" onClick={() => openWaitlist('avec')} className="mt-1 inline-flex items-center gap-1 bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded-full text-[11px] font-bold" data-testid={`hero-waitlist-${week.key}-avec`}>⏰ Liste d&apos;attente</button>
                                )}
                                {(trajets.aller.restantes <= 0) !== (trajets.retour.restantes <= 0) && <span className="block text-[10px] text-gray-500 font-normal">un seul trajet possible</span>}
                              </div>
                            ) : avecSpots === 0 ? (
                              <button type="button" onClick={() => openWaitlist('avec')}
                                className="inline-flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 text-white px-3 py-1.5 rounded-full text-xs font-bold transition shadow-md hover:shadow-lg hover:scale-105"
                                data-testid={`hero-waitlist-${week.key}-avec`}
                                title={`S'inscrire en liste d'attente pour ${week.label} — AVEC transport`}>
                                ⏰ Liste d&apos;attente
                              </button>
                            ) : spotLabel(avecSpots)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-1 text-[11px] text-gray-500">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span>Disponible</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-400"></span>Moins de 10 places</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span>Moins de 5 places / complet</span>
                  <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500"></span>Liste d&apos;attente</span>
                </div>
                {trip.spots_updated_at && (
                  <span data-testid="hero-spots-updated-date">
                    Mis à jour le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(trip.spots_updated_at))}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Iter 206 (Kamel) — Encart responsabilité « formule sans transport » sur toutes les
          fiches Espagne (4 jours, 8 jours, années suivantes) : trajet et stationnement
          hors responsabilité de l'association. Détection pérenne par titre/destination. */}
      {(`${trip.title || ''} ${trip.destination || ''}`.toLowerCase().includes('lloret')
        || `${trip.title || ''} ${trip.destination || ''}`.toLowerCase().includes('espagne')) && (
        <div className="bg-amber-50 border-y border-amber-200 py-4" data-testid="sans-transport-disclaimer">
          <div className="container-custom">
            <div className="max-w-5xl mx-auto flex items-start gap-3">
              <span className="text-2xl flex-shrink-0" aria-hidden="true">🚗</span>
              <div className="text-sm text-amber-900 leading-relaxed">
                <p className="font-bold">Petit rappel pour les voyageurs en formule « sans transport »</p>
                <p className="mt-1">
                  Vous organisez librement votre trajet jusqu'à la destination ainsi que votre stationnement sur place.
                  Nous essayons de vous donner des conseils et notre avis, en fonction de notre connaissance et des
                  informations qui nous sont transmises — mais <strong>cette partie de votre voyage ne relève en aucun
                  cas de la responsabilité de l'association</strong>. Merci de votre compréhension 🙏
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Photo Gallery Thumbnails */}
      {allImages.length > 1 && (
        <div className="bg-gray-100 py-4" data-testid="gallery-section">
          <div className="container-custom">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              {allImages.map((img, index) => (
                <button
                  key={index}
                  data-testid={`gallery-thumb-${index}`}
                  onClick={() => openLightbox(index)}
                  className="flex-shrink-0 relative rounded-lg overflow-hidden h-20 w-28 group transition-all duration-200 hover:ring-2 hover:ring-[#5BC5D4] focus:ring-2 focus:ring-[#5BC5D4] focus:outline-none"
                >
                  <img
                    src={img}
                    alt={`${trip.destination} - Photo ${index + 1}`}
                    className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <div className="card p-6">
                <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
                  <h2 className="text-2xl font-bold text-[#1E3A5F]">Description</h2>
                  {/* Iter 78sex — Téléchargement PDF descriptif (visible publiquement) */}
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/trips/${trip.id}/descriptif-pdf`}
                    download
                    data-testid="download-descriptif-pdf-btn"
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm bg-[#1E3A5F] text-white px-3 py-2 rounded-lg font-semibold hover:bg-[#2d5a8f] transition shadow-sm"
                    title="Télécharger le descriptif complet du voyage en PDF"
                  >
                    <span aria-hidden>📄</span> Télécharger le PDF
                  </a>
                </div>
                <p className="text-gray-700 text-lg leading-relaxed">{trip.description}</p>
                {trip.tarif_note && (
                  <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Important :</strong> {trip.tarif_note}
                  </div>
                )}
                {/* Iter 152ae (Kamel) — Précisions hébergement (visible sur la page voyage publique) */}
                {trip.precisions_hebergement && (
                  <div className="mt-4 bg-cyan-50 border-l-4 border-cyan-500 rounded-lg p-4 text-sm text-cyan-900" data-testid="trip-precisions-hebergement">
                    <div className="flex items-start gap-2">
                      <span className="text-xl">🛏️</span>
                      <div>
                        <strong className="block text-cyan-800 mb-1">Bon à savoir – Hébergement</strong>
                        <p className="whitespace-pre-line">{trip.precisions_hebergement}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Weekly Pricing Table — only for trips with weekly_pricing */}
              {trip.weekly_pricing && trip.weekly_pricing.length > 0 && (() => {
                const fusionMode = trip.weekly_pricing.some(w => w.fusion);
                return (
                <div className="card p-6" data-testid="weekly-pricing-section">
                  <h2 className="text-2xl font-bold mb-1 text-[#1E3A5F]">{fusionMode ? `🏨 Forfait hôtel par ${motSejour(trip)} — SANS transport` : `Tarifs par ${motSejour(trip)} — Prix de base`}</h2>
                  {fusionMode ? (
                    <p className="text-xs text-gray-600 mb-4" data-testid="pricing-fusion-intro">
                      <strong className="text-[#1E3A5F]">Le tarif le moins cher de France :</strong> prix par personne du <strong>séjour à l'hôtel en pension complète, transport non compris</strong>{new Set(weeksRows.map(w => w.price)).size === 1 ? <> : <strong>{weeksRows[0].price} €</strong>{weeksRows.length > 1 ? ', même prix pour chaque séjour' : ''}</> : <>, à partir de <strong>{Math.min(...trip.weekly_pricing.map(w => w.price))} €</strong></>}.
                      {legPrice ? <> Le car de l'association s'ajoute <strong>en option</strong> — voir le bloc Transport juste en dessous.</> : null}
                      <span className="block italic text-gray-500 mt-1"><TexteRiche texte={TP('tarif_note')} /></span>
                    </p>
                  ) : <p className="text-xs text-gray-500 italic mb-4"><TexteRiche texte={TP('tarif_note')} /></p>}
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className={`grid ${fusionMode ? 'grid-cols-2' : 'grid-cols-3'} bg-[#1E3A5F] text-white font-semibold text-sm`}>
                      <div className="px-4 py-3 border-r border-white/20">{estCourtSejour(trip) ? 'Séjour' : 'Semaine'}</div>
                      <div className={`px-4 py-3 text-center ${fusionMode ? '' : 'border-r border-white/20'}`}>{fusionMode ? 'Forfait hôtel / personne — sans transport' : 'Sans transport'}</div>
                      {!fusionMode && <div className="px-4 py-3 text-center">Avec transport</div>}
                    </div>
                    {weeksRows.map((week, i) => (
                      <div key={i} className={`grid ${fusionMode ? 'grid-cols-2' : 'grid-cols-3'} text-sm ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50/50 transition-colors`} data-testid={`pricing-row-${i}`}>
                        <div className="px-4 py-3 border-r border-gray-100 font-medium text-gray-700">{week.label}{week.fusion && !week.transport_price_per_leg && <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide text-gray-400">sans car</span>}</div>
                        <div className={`px-4 py-3 text-center font-bold text-[#1E3A5F] ${fusionMode ? '' : 'border-r border-gray-100'}`}>{week.price}&euro;</div>
                        {!fusionMode && (
                          <div className="px-4 py-3 text-center font-bold">
                            {week.transport_price ? <span className="text-[#5BC5D4]">{week.transport_price}&euro;</span>
                              : <span className="text-gray-400 text-xs font-normal italic">Non disponible</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {/* Iter 794 (Kamel) — comparatif Booking = semaine d'ÉTÉ (7 nuits) : jamais sur les séjours courts de mai (3 nuits) */}
                  {fusionMode && !estCourtSejour(trip) && devis.comparatif && (() => {
                    const cmp = devis.comparatif;
                    // Iter 796 (Kamel) — comparer la MÊME semaine : prix de la semaine relevée (ex. 14 → 21 août = 540 €), sinon la moins chère
                    const norm = (t) => (t || '').toLowerCase().replace(/\s+/g, ' ').replace(/->|→|–|-|au\b/g, '→').replace(/\s*→\s*/g, '→').trim();
                    const semaineRelevee = cmp.booking_semaine ? trip.weekly_pricing.find(w => norm(w.label) === norm(cmp.booking_semaine) || norm(w.label).startsWith(norm(cmp.booking_semaine).split('→')[0])) : null;
                    const nous = semaineRelevee ? semaineRelevee.price : Math.min(...trip.weekly_pricing.map(w => w.price));
                    const pct = Math.round((1 - nous / cmp.booking_prix) * 100);
                    // Iter 796 — pas de nom de marque : « une grande plateforme de réservation en ligne »
                    return pct > 0 && (
                      <div className="mt-4 rounded-xl border-2 border-[#FFC400] bg-amber-50 p-4" data-testid="comparatif-booking">
                        <p className="text-xs uppercase tracking-wide font-black text-[#1E3A5F]">Comparez avant de réserver</p>
                        <p className="text-3xl font-black text-[#1E3A5F] mt-1">−{pct} %</p>
                        <p className="text-sm text-gray-800 mt-1">
                          Sur une grande plateforme de réservation en ligne, la semaine {semaineRelevee ? <strong>{semaineRelevee.label}</strong> : 'comparable'} à l'Oasis Park en pension complète est affichée à <strong>{cmp.booking_prix} € par personne</strong>.
                          Avec l'association, la même semaine : <strong>{nous} €</strong>, avec les mêmes repas, les mêmes piscines, les animations et nos accompagnateurs.
                        </p>
                        <p className="text-[11px] text-gray-500 mt-1 italic">{cmp.booking_note}{cmp.booking_date ? ` · prix relevé le ${new Date(cmp.booking_date).toLocaleDateString('fr-FR')}` : ''}. Hors frais d'adhésion.</p>
                      </div>
                    );
                  })()}
                </div>
                );
              })()}

              {/* Room Pricing Table — for trips with room_pricing (like New York) */}
              {trip.room_pricing && trip.room_pricing.length > 0 && (
                <div className="card p-6" data-testid="room-pricing-section">
                  <h2 className="text-2xl font-bold mb-1 text-[#1E3A5F]">Tarifs par type de chambre — Prix de base</h2>
                  <p className="text-xs text-gray-500 italic mb-4">Les réductions ou suppléments éventuels ne sont pas calculés ici.</p>
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="grid grid-cols-2 bg-[#1E3A5F] text-white font-semibold text-sm">
                      <div className="px-4 py-3 border-r border-white/20">Type de chambre</div>
                      <div className="px-4 py-3 text-center">Tarif / personne</div>
                    </div>
                    {trip.room_pricing.map((room, i) => (
                      <div key={i} className={`grid grid-cols-2 text-sm ${room.base ? 'bg-[#5BC5D4]/10 border-l-4 border-[#5BC5D4]' : i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50/50 transition-colors`} data-testid={`room-pricing-row-${i}`}>
                        <div className="px-4 py-3 border-r border-gray-100 font-medium text-gray-700 flex items-center gap-2">
                          {room.base && <span className="text-[10px] bg-[#5BC5D4] text-white px-1.5 py-0.5 rounded font-bold">BASE</span>}
                          <span>{room.type}</span>
                        </div>
                        <div className="px-4 py-3 text-center font-bold text-[#1E3A5F] text-lg">{room.price}&euro;</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Réductions et Suppléments — only for trips with reductions */}
              {trip.reductions && trip.reductions.length > 0 && (
                <div className="card p-6" data-testid="reductions-section">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Réductions et suppléments</h2>
                  <div className="space-y-3">
                    {trip.reductions.map((item, index) => {
                      const isReduction = item.toLowerCase().startsWith('réduction');
                      return (
                        <div key={index} className={`flex items-start gap-3 p-3 rounded-lg ${isReduction ? 'bg-green-50 border border-green-200' : 'bg-amber-50 border border-amber-200'}`}>
                          <span className={`text-lg flex-shrink-0 mt-0.5 ${isReduction ? 'text-green-500' : 'text-amber-500'}`}>
                            {isReduction ? '\u2212' : '\u002B'}
                          </span>
                          <span className="text-sm text-gray-700">{item}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Transport en option — highlighted box */}
              {trip.transport_option && (
                <div className="card p-0 overflow-hidden border-2 border-[#5BC5D4]" data-testid="transport-option-section">
                  <div className="bg-gradient-to-r from-[#5BC5D4] to-[#4AB4C3] text-white px-6 py-4">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
                      {trip.transport_option.price_per_leg ? <>Car en option : {trip.transport_option.price_per_leg * 2}&euro; aller-retour par personne {!estCourtSejour(trip) && <span className="text-sm font-semibold text-white/90">({trip.transport_option.price_per_leg} € l'aller · {trip.transport_option.price_per_leg} € le retour)</span>}</> : <>Transport en option : +{trip.transport_option.price_per_week}&euro; / semaine</>}
                    </h2>
                    <p className="text-sm text-white/90 mt-1">{trip.transport_option.description}</p>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] text-sm mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z"/></svg>
                        Villes de départ et horaires
                      </h3>
                      <div className="space-y-2">
                        {trip.transport_option.departures.map((dep, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm bg-gray-50 rounded-lg px-4 py-2" data-testid={`transport-departure-${i}`}>
                            <span className="font-bold text-[#5BC5D4] bg-[#5BC5D4]/10 px-2 py-0.5 rounded text-xs whitespace-nowrap min-w-[50px] text-center">{dep.time}</span>
                            <div>
                              <span className="font-semibold text-gray-800">{dep.city}</span>
                              <span className="block text-gray-500 text-xs">{dep.address}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {trip.transport_option.note && (
                      <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4]/30 rounded-lg p-3 text-xs text-gray-700">
                        <strong>Bonus :</strong> {trip.transport_option.note}
                      </div>
                    )}
                    {famille2Semaines && (
                      <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="famille-2-semaines">
                        <p className="text-xs uppercase tracking-wide font-black text-emerald-900">🚌 Le car se réserve par trajet — exemple : famille de 4, deux semaines</p>
                        <p className="text-3xl font-black text-emerald-800 mt-1">{legPrice * 2 * 4} € d'économie sur le transport</p>
                        <p className="text-sm text-gray-800 mt-1">
                          Aller sur la 1re semaine ({famille2Semaines[0].label}), retour sur la 2e ({famille2Semaines[1].label}) : vous payez {legPrice} € + {legPrice} € par personne
                          au lieu de {legPrice * 2} € × 2 semaines — <strong>{legPrice * 2} € de moins par personne</strong>, soit {legPrice * 2 * 4} € pour 4. L'hôtel, lui, se paie semaine par semaine.
                        </p>
                      </div>
                    )}
                    {airesInfo}
                    {monBusPromo}
                  </div>
                </div>
              )}

              {trip.chambres_infos && (
                <div className="card p-5 border-l-4 border-amber-400 bg-amber-50/40" data-testid="chambres-infos">
                  <h2 className="text-lg font-bold text-[#1E3A5F] mb-1" data-testid="chambres-infos-titre">{trip.chambres_infos_titre?.trim() || '🛏️ Bon à savoir — les chambres'}</h2>
                  <p className="text-sm text-gray-800 leading-relaxed whitespace-pre-line">{trip.chambres_infos}</p>
                </div>
              )}
              {trip.weekly_pricing?.some(w => w.fusion) && (
                <div className="card p-5 border-2 border-sky-300 bg-gradient-to-br from-sky-50 to-white" data-testid="vue-piscine-bloc">
                  <div className="flex items-start gap-3">
                    <span className="text-3xl">🏊</span>
                    <div>
                      <p className="text-[11px] font-black tracking-[0.2em] uppercase text-sky-700">Option · {estCourtSejour(trip) ? '+40 €' : '+80 €'} par chambre et par {motSejour(trip)}</p>
                      <h2 className="text-lg font-bold text-[#1E3A5F] mt-0.5">Vue piscine garantie</h2>
                      {trip.vue_piscine_texte ? (
                        <p className="text-sm text-gray-800 leading-relaxed mt-1 whitespace-pre-line" data-testid="vue-piscine-texte">{trip.vue_piscine_texte}</p>
                      ) : (
                        <p className="text-sm text-gray-800 leading-relaxed mt-1" data-testid="vue-piscine-texte">
                          Comme son nom l'indique : avec cette option, votre chambre <strong>donne sur la piscine, c'est garanti</strong> — vous le savez avant de partir, pas en arrivant.
                          Sans l'option, l'hôtel attribue les chambres selon ses disponibilités (vue piscine, jardin ou rue). Elle se coche chambre par chambre, au moment de l'inscription ou du devis.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {trip.weekly_pricing?.some(w => w.fusion) && <ALaCarteBloc />}
              {trip.spa_flash && <SpaBloc />}
              {/* Iter 749 (Kamel) — vitrine des excursions sur place, APRÈS la description / tarifs / transport */}
              <ExcursionsPublicSection destinationTexte={`${trip.title || ''} ${trip.destination || ''}`} tripId={trip.id} />

              {/* Highlights */}
              {trip.highlights && trip.highlights.length > 0 && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Points forts</h2>
                  <ul className="space-y-2">
                    {trip.highlights.map((highlight, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <svg className="w-6 h-6 text-[#5BC5D4] flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Flight Info - highlighted box (supports single and multi-leg) */}
              {trip.flight_info && (
                <div className="card p-0 overflow-hidden border-2 border-[#1E3A5F]" data-testid="flight-info-section">
                  <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2A4A6F] text-white px-6 py-4">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>
                      Plan de vol - {trip.flight_info.airline}
                    </h2>
                  </div>
                  <div className="p-6 space-y-4">
                    {/* Multi-leg outbound flights */}
                    {trip.flight_info.outbound_legs ? (
                      <div className="space-y-3">
                        <div className="text-xs font-bold text-[#1E3A5F] uppercase tracking-wide">Vol aller</div>
                        {trip.flight_info.outbound_legs.map((leg, i) => (
                          <div key={i} className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                            <div className="text-[10px] font-semibold text-blue-600 uppercase mb-2">{leg.date}{leg.note ? ` (${leg.note})` : ''}</div>
                            <div className="flex items-center gap-4">
                              <div className="text-center min-w-[60px]">
                                <div className="text-lg font-bold text-[#1E3A5F]">{leg.departure_time}</div>
                                <div className="text-xs text-gray-600">{leg.from_city}</div>
                              </div>
                              <div className="flex-1 flex items-center gap-1">
                                <div className="h-px bg-[#5BC5D4] flex-1"></div>
                                <svg className="w-4 h-4 text-[#5BC5D4] flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                                <div className="h-px bg-[#5BC5D4] flex-1"></div>
                              </div>
                              <div className="text-center min-w-[60px]">
                                <div className="text-lg font-bold text-[#1E3A5F]">{leg.arrival_time}</div>
                                <div className="text-xs text-gray-600">{leg.to_city}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : trip.flight_info.outbound && (
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                        <div className="text-xs font-bold text-[#1E3A5F] uppercase tracking-wide mb-2">Vol aller - {trip.flight_info.outbound.date}</div>
                        <div className="flex items-center gap-4">
                          <div className="text-center"><div className="text-xl font-bold text-[#1E3A5F]">{trip.flight_info.outbound.departure_time}</div><div className="text-sm text-gray-600">{trip.flight_info.outbound.from_city}</div></div>
                          <div className="flex-1 flex items-center gap-2"><div className="h-px bg-[#5BC5D4] flex-1"></div><svg className="w-5 h-5 text-[#5BC5D4]" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg><div className="h-px bg-[#5BC5D4] flex-1"></div></div>
                          <div className="text-center"><div className="text-xl font-bold text-[#1E3A5F]">{trip.flight_info.outbound.arrival_time}</div><div className="text-sm text-gray-600">{trip.flight_info.outbound.to_city}</div></div>
                        </div>
                      </div>
                    )}
                    {/* Internal flight (single) */}
                    {trip.flight_info.internal && (
                      <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-600 text-center">
                        <svg className="w-4 h-4 inline mr-1 text-[#5BC5D4]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4"/></svg>
                        {trip.flight_info.internal.description}
                      </div>
                    )}
                    {/* Multi-leg return flights */}
                    {trip.flight_info.return_legs ? (
                      <div className="space-y-3">
                        <div className="text-xs font-bold text-amber-800 uppercase tracking-wide">Vol retour</div>
                        {trip.flight_info.return_legs.map((leg, i) => (
                          <div key={i} className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                            <div className="text-[10px] font-semibold text-amber-600 uppercase mb-2">{leg.date}{leg.note ? ` (${leg.note})` : ''}</div>
                            <div className="flex items-center gap-4">
                              <div className="text-center min-w-[60px]">
                                <div className="text-lg font-bold text-[#1E3A5F]">{leg.departure_time}</div>
                                <div className="text-xs text-gray-600">{leg.from_city}</div>
                              </div>
                              <div className="flex-1 flex items-center gap-1">
                                <div className="h-px bg-amber-400 flex-1"></div>
                                <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg>
                                <div className="h-px bg-amber-400 flex-1"></div>
                              </div>
                              <div className="text-center min-w-[60px]">
                                <div className="text-lg font-bold text-[#1E3A5F]">{leg.arrival_time}</div>
                                <div className="text-xs text-gray-600">{leg.to_city}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : trip.flight_info.return_flight && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                        <div className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-2">Vol retour - {trip.flight_info.return_flight.date}</div>
                        <div className="flex items-center gap-4">
                          <div className="text-center"><div className="text-xl font-bold text-[#1E3A5F]">{trip.flight_info.return_flight.departure_time}</div><div className="text-sm text-gray-600">{trip.flight_info.return_flight.from_city}</div></div>
                          <div className="flex-1 flex items-center gap-2"><div className="h-px bg-amber-400 flex-1"></div><svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/></svg><div className="h-px bg-amber-400 flex-1"></div></div>
                          <div className="text-center"><div className="text-xl font-bold text-[#1E3A5F]">{trip.flight_info.return_flight.arrival_time}</div><div className="text-sm text-gray-600">{trip.flight_info.return_flight.to_city}</div></div>
                        </div>
                      </div>
                    )}
                    {/* Meeting point */}
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <div className="font-bold text-red-800 mb-1">Point de rendez-vous</div>
                      <p className="text-red-700">{trip.flight_info.meeting_point} - <strong>{trip.flight_info.meeting_time}</strong></p>
                    </div>
                    {/* Luggage */}
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm text-gray-700 flex items-start gap-2">
                      <svg className="w-5 h-5 text-[#5BC5D4] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/></svg>
                      <span><strong>Bagages :</strong> {trip.flight_info.luggage}</span>
                    </div>
                    {trip.flight_info.note && (
                      <div className="text-xs text-gray-500 text-center italic">{trip.flight_info.note}</div>
                    )}
                    {/* Formalities */}
                    {trip.flight_info.formalities && (
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm flex items-start gap-2">
                        <svg className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"/></svg>
                        <span><strong>Formalités :</strong> {trip.flight_info.formalities}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hotels */}
              {trip.meal_plan && (
                <div className="card p-5 border-l-4 border-emerald-400 bg-emerald-50/30" data-testid="repas-section">
                  <h2 className="text-lg font-bold text-[#1E3A5F] mb-1">🍴 Repas</h2>
                  <p className="text-sm font-semibold text-gray-800" data-testid="repas-label">{formuleRepasLabel(trip) || REPAS.non}</p>
                  {codeRepas(trip.meal_plan) === 'par_etape' && etapesRepas(trip).length > 0 && (
                    <ul className="mt-2 space-y-1 text-sm text-gray-700" data-testid="repas-etapes">
                      {etapesRepas(trip).map((e, i) => <li key={i}>• <strong>{e.lieu || e.nom}</strong>{e.nights ? ` (${e.nights} nuit${e.nights > 1 ? 's' : ''})` : ''} : {e.label}</li>)}
                    </ul>
                  )}
                  {trip.transport === 'avion' && <p className="text-[11px] text-gray-500 mt-2">Les repas à bord de l'avion dépendent de la compagnie et ne font pas partie de la formule de l'hôtel.</p>}
                </div>
              )}
              {trip.hotels && trip.hotels.length > 0 && (
                <div className="card p-6" data-testid="hotels-section">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Hébergement</h2>
                  <div className="space-y-3">
                    {trip.hotels.map((hotel, i) => (
                      <div key={i} className="flex items-start gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100" data-testid={`hotel-${i}`}>
                        <div className="bg-[#5BC5D4] rounded-full p-2.5 flex-shrink-0 mt-1">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>
                        </div>
                        <div className="flex-1">
                          <div className="font-bold text-[#1E3A5F] text-sm">{hotel.name}{hotel.stars ? ` ${hotel.stars}*` : ''}</div>
                          <div className="text-xs text-gray-500">{hotel.location}{hotel.nights ? ` - ${hotel.nights} nuit${hotel.nights > 1 ? 's' : ''}` : ''}</div>
                          {REPAS[codeRepas(hotel.meal_plan)] && <div className="text-xs font-semibold text-emerald-700 mt-0.5" data-testid={`hotel-meal-${i}`}>🍴 {REPAS[codeRepas(hotel.meal_plan)]}</div>}
                          {hotel.details && <div className="text-xs text-gray-600 mt-1">{hotel.details}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Iter 678 — Carte du circuit (croisières) : image fournie, affichée au-dessus de l'itinéraire */}
              {trip.itineraire_image && (
                <div className="card p-4 sm:p-6" data-testid="itineraire-image-section">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Le circuit</h2>
                  <a href={trip.itineraire_image} target="_blank" rel="noopener noreferrer" title="Agrandir" data-testid="itineraire-image-link">
                    <img src={trip.itineraire_image} alt={`Carte du circuit — ${trip.title}`} className="w-full rounded-xl border border-gray-100 shadow-sm" loading="lazy" data-testid="itineraire-image" />
                  </a>
                  <p className="text-xs text-gray-400 mt-2 text-center">Touchez la carte pour l'agrandir</p>
                </div>
              )}

              {/* Itinerary - Day by day / section by section */}
              {trip.itinerary && trip.itinerary.length > 0 && (
                <div className="card p-6" data-testid="itinerary-section">
                  <h2 className="text-2xl font-bold mb-6 text-[#1E3A5F]">Itinéraire jour par jour</h2>
                  <div className="space-y-0">
                    {trip.itinerary.map((day, i) => (
                      <div key={i} className="relative" data-testid={`itinerary-day-${day.day}`}>
                        {/* Timeline connector */}
                        {i < trip.itinerary.length - 1 && (
                          <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-[#5BC5D4]/20"></div>
                        )}
                        <div className="flex gap-4 pb-6">
                          <div className="flex-shrink-0">
                            {day.icon ? (
                              <div className={`rounded-full w-10 h-10 flex items-center justify-center shadow-md ${
                                day.icon === 'plane' ? 'bg-gradient-to-br from-[#1E3A5F] to-[#2A4A6F]' :
                                day.icon === 'city' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                                day.icon === 'beach' ? 'bg-gradient-to-br from-[#5BC5D4] to-[#4AB4C3]' :
                                day.icon === 'island' ? 'bg-gradient-to-br from-emerald-500 to-emerald-600' :
                                day.icon === 'desert' ? 'bg-gradient-to-br from-amber-500 to-orange-500' :
                                day.icon === 'monument' ? 'bg-gradient-to-br from-rose-500 to-rose-600' :
                                'bg-gradient-to-br from-[#5BC5D4] to-[#4AB4C3]'
                              }`}>
                                {day.icon === 'plane' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>}
                                {day.icon === 'city' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/></svg>}
                                {day.icon === 'beach' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>}
                                {day.icon === 'island' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>}
                                {day.icon === 'desert' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>}
                                {day.icon === 'monument' && <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"/></svg>}
                              </div>
                            ) : (
                              <div className="bg-gradient-to-br from-[#5BC5D4] to-[#4AB4C3] text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-sm shadow-md">
                                J{day.day}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-[#5BC5D4] bg-[#5BC5D4]/10 px-2 py-0.5 rounded">{day.date}</span>
                              <h3 className="font-bold text-[#1E3A5F] text-sm">{day.title}</h3>
                            </div>
                            {/* Description (for destination stays) */}
                            {day.description && (
                              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{day.description}</p>
                            )}
                            {/* Hotel info badge */}
                            {day.hotel_info && (
                              <div className="bg-[#5BC5D4]/10 border border-[#5BC5D4]/20 rounded-lg px-3 py-2 text-xs text-[#1E3A5F] mb-3 flex items-center gap-2">
                                <svg className="w-4 h-4 text-[#5BC5D4] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5"/></svg>
                                <span className="font-medium">{day.hotel_info}</span>
                              </div>
                            )}
                            {/* Events */}
                            <ul className="space-y-1.5">
                              {day.events.map((event, j) => (
                                <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                                  <span className="text-[#5BC5D4] flex-shrink-0 mt-1">&#9679;</span>
                                  <span>{event}</span>
                                </li>
                              ))}
                            </ul>
                            {/* Activities by category (for destination stays) */}
                            {day.activities && day.activities.length > 0 && (
                              <div className="mt-4 pt-3 border-t border-gray-100">
                                <div className="text-xs font-bold text-[#1E3A5F] mb-2">Activités et excursions en option</div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  {day.activities.map((cat, k) => (
                                    <div key={k} className="bg-gray-50 rounded-lg p-2.5">
                                      <div className="text-xs font-semibold text-[#5BC5D4] mb-1">{cat.category}</div>
                                      <ul className="space-y-0.5">
                                        {cat.items.map((item, l) => (
                                          <li key={l} className="text-xs text-gray-600 flex items-start gap-1">
                                            <span className="text-[#5BC5D4]">-</span> {item}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Desert Activities */}
              {trip.desert_activities && trip.desert_activities.length > 0 && (
                <div className="card p-0 overflow-hidden border-2 border-amber-300" data-testid="desert-activities-section">
                  <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-6 py-4">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"/></svg>
                      Activités incluses dans le désert
                    </h2>
                  </div>
                  <div className="p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {trip.desert_activities.map((activity, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm bg-amber-50/50 rounded-lg px-3 py-2" data-testid={`desert-activity-${i}`}>
                          <span className="text-amber-500 flex-shrink-0 mt-0.5">&#10003;</span>
                          <span className="text-gray-700">{activity}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Car Schedule (included transport for autocar trips) */}
              {trip.car_schedule && (
                <div className="card p-0 overflow-hidden border-2 border-[#5BC5D4]" data-testid="car-schedule-section">
                  <div className="bg-gradient-to-r from-[#5BC5D4] to-[#4AB4C3] text-white px-6 py-4">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"/></svg>
                      Horaires de départ et retour en autocar
                    </h2>
                  </div>
                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] text-sm mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 1.414L10.586 9H7a1 1 0 100 2h3.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414z" clipRule="evenodd"/></svg>
                        Aller
                      </h3>
                      <div className="space-y-2">
                        {(trip.car_schedule.aller || []).map((dep, i) => (
                          <div key={i} className="flex items-start gap-3 text-sm bg-green-50 rounded-lg px-4 py-2">
                            <span className="font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded text-xs whitespace-nowrap min-w-[50px] text-center">{dep.time}</span>
                            <div>
                              <span className="font-semibold text-gray-800">{dep.city}</span>
                              {dep.address && <span className="block text-gray-500 text-xs">{dep.address}</span>}
                              {dep.note && <span className="block text-xs text-amber-600 italic">{dep.note}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1E3A5F] text-sm mb-3 flex items-center gap-2">
                        <svg className="w-4 h-4 text-amber-500" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm.707-10.293a1 1 0 00-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L9.414 11H13a1 1 0 100-2H9.414l1.293-1.293z" clipRule="evenodd"/></svg>
                        Retour
                      </h3>
                      <div className="space-y-2">
                        {!(trip.car_schedule.retour || []).length && trip.car_schedule.retour_heure && (
                          <div className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-gray-700" data-testid="car-retour-heure">Départ du retour : <strong>{trip.car_schedule.retour_heure}</strong> — dépose aux mêmes points, dans l'ordre inverse.</div>
                        )}
                        {(trip.car_schedule.retour || []).map((dep, i) => {
                          const isInfoBlock = dep.time && dep.time.length > 20;
                          if (isInfoBlock) {
                            return (
                              <div key={i} className="text-sm bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-gray-700 italic">
                                {dep.time}
                              </div>
                            );
                          }
                          return (
                            <div key={i} className="flex items-start gap-3 text-sm bg-amber-50 rounded-lg px-4 py-2">
                              <span className="font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded text-xs whitespace-nowrap min-w-[50px] text-center">{dep.time}</span>
                              <div>
                                <span className="font-semibold text-gray-800">{dep.city || ''}</span>
                                {dep.address && <span className="block text-gray-500 text-xs">{dep.address}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {trip.car_schedule.note && (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 italic">{trip.car_schedule.note}</div>
                    )}
                    {airesInfo}
                    {monBusPromo}
                  </div>
                </div>
              )}

              {/* Iter 759 (Kamel) — UN bloc Transport pour les voyages en car sans transport_option / car_schedule : horaires + MonBus */}
              {isCarTrip && !trip.transport_option && !trip.car_schedule && (
                <div className="card p-0 overflow-hidden border-2 border-[#5BC5D4]" data-testid="transport-car-section">
                  <div className="bg-gradient-to-r from-[#5BC5D4] to-[#4AB4C3] text-white px-6 py-4">
                    <h2 className="text-xl font-bold flex items-center gap-2">🚌 Transport en autocar</h2>
                    <p className="text-sm text-white/90 mt-1"><TexteRiche texte={TP('car_compris')} /></p>
                  </div>
                  <div className="p-6 space-y-4">
                    {trip.departure_schedule?.length > 0 && (
                      <div data-testid="departure-schedule">
                        <h3 className="font-bold text-[#1E3A5F] text-sm mb-3">Horaires et lieux de départ</h3>
                        <div className="space-y-2">
                          {trip.departure_schedule.map((dep, i) => (
                            <div key={i} className="flex items-start gap-3 text-sm bg-gray-50 rounded-lg px-4 py-2" data-testid={`departure-${i}`}>
                              <span className="font-bold text-[#5BC5D4] bg-[#5BC5D4]/10 px-2 py-0.5 rounded text-xs whitespace-nowrap min-w-[50px] text-center">{dep.time}</span>
                              <div>
                                <span className="font-semibold text-gray-800">{dep.city}</span>
                                <span className="block text-gray-500 text-xs">{dep.address}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {airesInfo}
                    {monBusPromo}
                  </div>
                </div>
              )}

              {/* Activity Suggestions (day trips) */}
              {trip.activity_suggestions && trip.activity_suggestions.length > 0 && (
                <div className="card p-6" data-testid="activity-suggestions-section">
                  <h2 className="text-2xl font-bold mb-2 text-[#1E3A5F]">Idées de visites et activités</h2>
                  <p className="text-sm text-gray-500 mb-4">Pour profiter au maximum de votre journée :</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {trip.activity_suggestions.map((act, i) => (
                      <div key={i} className="bg-gradient-to-br from-gray-50 to-white border border-gray-100 rounded-xl p-3 hover:shadow-md transition-shadow" data-testid={`suggestion-${i}`}>
                        <div className="font-bold text-[#1E3A5F] text-sm mb-0.5">{act.name}</div>
                        <div className="text-xs text-gray-600">{act.desc}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Paid Options */}
              {trip.paid_options && trip.paid_options.length > 0 && (
                <div className="card p-6" data-testid="paid-options-section">
                  <h2 className="text-lg font-bold mb-3 text-[#1E3A5F]">Options payantes disponibles</h2>
                  <div className="space-y-2">
                    {trip.paid_options.map((opt, i) => (
                      <div key={i} className="flex items-start gap-3 bg-purple-50 border border-purple-100 rounded-lg p-3">
                        <span className="text-purple-500 font-bold flex-shrink-0">+</span>
                        <div>
                          <span className="font-semibold text-sm text-gray-800">{opt.name}</span>
                          {opt.desc && <span className="block text-xs text-gray-600">{opt.desc}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Excursions en option (Lloret etc) */}
              {(trip.excursions_image_url || (trip.excursions && trip.excursions.length > 0)) && (
                <div className="card p-6" data-testid="excursions-section">
                  <h2 className="text-2xl font-bold mb-2 text-[#1E3A5F]">Excursions en option</h2>
                  <p className="text-sm text-gray-500 mb-4"><TexteRiche texte={TP('excursions_intro')} /></p>
                  {trip.excursions_image_url ? (
                    <div className="mt-2">
                      <a href={trip.excursions_image_url} target="_blank" rel="noopener noreferrer" data-testid="excursions-image-link">
                        <img
                          src={trip.excursions_image_url}
                          alt="Programme complet des excursions"
                          className="w-full rounded-xl shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                          data-testid="excursions-image"
                        />
                      </a>
                      <p className="text-xs text-gray-400 mt-2 text-center">Cliquez sur l'image pour l'agrandir</p>
                    </div>
                  ) : (
                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="grid grid-cols-12 bg-[#1E3A5F] text-white font-semibold text-xs">
                        <div className="col-span-2 px-3 py-2.5 border-r border-white/20">Jour</div>
                        <div className="col-span-4 px-3 py-2.5 border-r border-white/20">Destination</div>
                        <div className="col-span-3 px-3 py-2.5 border-r border-white/20">Adulte</div>
                        <div className="col-span-3 px-3 py-2.5">Enfant</div>
                      </div>
                      {trip.excursions.map((exc, i) => (
                        <div key={i} className={`grid grid-cols-12 text-sm ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-blue-50/50 transition-colors`} data-testid={`excursion-row-${i}`}>
                          <div className="col-span-2 px-3 py-2.5 border-r border-gray-100 text-xs font-medium text-gray-500">{exc.jour}</div>
                          <div className="col-span-4 px-3 py-2.5 border-r border-gray-100">
                            <div className="font-semibold text-[#1E3A5F] text-sm">{exc.nom}</div>
                            <div className="text-xs text-gray-500">{exc.type}</div>
                          </div>
                          <div className="col-span-3 px-3 py-2.5 border-r border-gray-100 font-bold text-[#1E3A5F] text-sm">{exc.prix_adulte}</div>
                          <div className="col-span-3 px-3 py-2.5 font-bold text-[#1E3A5F] text-sm">{exc.prix_enfant}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {trip.formalities && !trip.flight_info && (
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm flex items-start gap-2" data-testid="formalities-section">
                  <svg className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2"/></svg>
                  <span><strong>Formalités :</strong> {trip.formalities}</span>
                </div>
              )}

              {/* Photo Gallery Grid (in content) */}
              {allImages.length > 1 && (
                <div className="card p-6" data-testid="gallery-grid-section">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Photos de la destination</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {allImages.slice(0, 6).map((img, index) => (
                      <button
                        key={index}
                        data-testid={`gallery-grid-${index}`}
                        onClick={() => openLightbox(index)}
                        className="relative rounded-xl overflow-hidden aspect-[4/3] group cursor-pointer"
                      >
                        <img
                          src={img}
                          alt={`${trip.destination} - Photo ${index + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <svg className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"/>
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Program Details — only if has content and no itinerary */}
              {trip.program_details && !trip.itinerary && (
                <div className="card p-6">
                  <h2 className="text-2xl font-bold mb-4 text-[#1E3A5F]">Votre séjour sur place</h2>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">{trip.program_details}</p>
                  
                  {trip.program_pdf_url && (
                    <div className="mt-6">
                      <button
                        type="button"
                        onClick={() => openInfo('pdf', `Programme complet — ${trip.title}`, trip.program_pdf_url)}
                        data-testid="download-pdf-btn"
                        className="inline-flex items-center gap-2 bg-[#5BC5D4] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#4AB4C3] transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Voir le programme complet (PDF)
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Included */}
              <div className="card p-6">
                <h2 className="text-2xl font-bold mb-4 text-[#5BC5D4]">Inclus dans le prix</h2>
                <ul className="space-y-2">
                  {trip.included.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-green-500 text-xl flex-shrink-0">&#10003;</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Not Included */}
              <div className="card p-6">
                <h2 className="text-2xl font-bold mb-4 text-gray-700">Non inclus</h2>
                <ul className="space-y-2">
                  {trip.not_included.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-red-500 text-xl flex-shrink-0">&#10007;</span>
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Iter 860 — vidéos YouTube de ce voyage (si la chaîne en a) */}
              <VideosYoutube tripId={trip.id} titre="En vidéo" limit={4} compact />

              {/* Phrase de protection - sur tous les voyages */}
              <div className="card p-4 border border-[#5BC5D4]/30 bg-[#5BC5D4]/5" data-testid="programme-disclaimer">
                <p className="text-sm text-gray-700 italic leading-relaxed flex items-start gap-2">
                  <svg className="w-5 h-5 text-[#5BC5D4] flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                  <span><TexteRiche texte={TP('programme_disclaimer')} /></span>
                </p>
              </div>

              {/* Important Notes */}
              {trip.important_notes && trip.important_notes.length > 0 && (
                <div className="card p-6 border-2 border-amber-200 bg-amber-50/30" data-testid="important-notes-section">
                  <h2 className="text-xl font-bold mb-4 text-amber-800 flex items-center gap-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.27 16.5c-.77.833.192 2.5 1.732 2.5z"/></svg>
                    Notes importantes
                  </h2>
                  <ul className="space-y-3">
                    {trip.important_notes.map((note, index) => (
                      <li key={index} className="flex items-start gap-3 text-sm text-gray-700 bg-white/60 rounded-lg p-3 border border-amber-100">
                        <span className="text-amber-500 font-bold flex-shrink-0">&#9679;</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              {/* Iter 759 (Kamel) — colonne « outils d'inscription » : toujours visible, avec SON propre défilement (les infos défilent à gauche) */}
              <div className="card p-6 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto" data-testid="trip-sidebar">
                <div className="text-center mb-4">
                  <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold mb-1">Prix de base</div>
                  <div className="text-xs text-gray-600 mb-2">
                    {trip.duration_days === 1 ? 'par personne' : (trip.weekly_pricing || multiDeparts) && !trip.prix_identique ? 'à partir de' : (trip.weekly_pricing || multiDeparts) ? 'par personne (chambre de 2) — même prix pour chaque départ' : 'par personne (chambre de 2)'}
                  </div>
                  <div className="text-4xl font-bold text-[#5BC5D4] mb-1" data-testid="trip-price">{prixAffiche}&euro;</div>
                  <div className="text-[11px] text-gray-400 italic leading-snug mb-2">Les réductions ou suppléments éventuels ne sont pas calculés ici.</div>
                  <div className="text-sm text-gray-600">{trip.price_info}</div>
                </div>
                {((trip.weekly_pricing && trip.weekly_pricing.length > 0) || isTripAvailable) && (
                  <a href="/inscription" data-testid="sidebar-register-top-btn"
                    className="mb-4 flex items-center justify-center gap-2 w-full bg-[#5BC5D4] hover:bg-[#4AB4C3] text-white font-bold py-3 px-4 rounded-xl transition-colors shadow-md">
                    ✍️ Je m'inscris en ligne
                  </a>
                )}

                <div className="space-y-4 mb-6">
                  {/* Date options (multiple dates like Turin) → replace blue dates card */}
                  {multiDeparts ? (
                    <DepartsChoix trip={trip} />
                  ) : trip.date_options && trip.date_options.length > 0 ? (
                    <div data-testid="date-options-section">
                      <div className="bg-gradient-to-r from-[#5BC5D4] to-[#3BA4B3] text-white rounded-lg p-4 mb-3">
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          <span className="font-semibold text-sm opacity-90">Dates du voyage</span>
                        </div>
                        <p className="text-xs text-white/80">Attention : chaque date a son propre statut de disponibilité</p>
                      </div>
                      <div className="space-y-2">
                        {trip.date_options.map((opt, i) => (
                          <div key={i} className={`flex items-center justify-between px-4 py-3 rounded-lg border-2 text-sm ${
                            opt.status === 'liste_attente' ? 'bg-orange-50 border-orange-200' :
                            opt.status === 'complet' ? 'bg-red-50 border-red-200' :
                            'bg-green-50 border-green-300 ring-1 ring-green-200'
                          }`} data-testid={`date-option-${i}`}>
                            <div>
                              <span className="font-bold text-gray-800 block">{opt.label}</span>
                              {opt.status === 'disponible' && (
                                <span className="text-xs text-green-700 font-medium">Inscription en ligne possible</span>
                              )}
                              {opt.status === 'liste_attente' && (
                                <span className="text-xs text-orange-600 font-medium">Contactez-nous par WhatsApp</span>
                              )}
                            </div>
                            {opt.status === 'liste_attente' || opt.status === 'complet' ? (
                              <span className="font-bold text-xs px-3 py-1 rounded-full bg-orange-100 text-orange-600 whitespace-nowrap">
                                LISTE D'ATTENTE
                              </span>
                            ) : (
                              <span className="font-bold text-xs px-3 py-1 rounded-full bg-green-200 text-green-700 whitespace-nowrap animate-pulse">
                                PLACES DISPONIBLES
                              </span>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* CTA pour les dates disponibles */}
                      {trip.date_options.some(d => d.status === 'disponible') && (
                        <Link to="/inscription" onClick={() => window.scrollTo(0, 0)}
                          className="mt-3 flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2.5 px-4 rounded-lg transition-colors shadow-sm"
                          data-testid="date-options-register-btn">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          S'inscrire pour {trip.date_options.filter(d => d.status === 'disponible').map(d => d.label).join(' / ')}
                        </Link>
                      )}

                      {/* CTA WhatsApp uniquement pour les dates en liste d'attente */}
                      {trip.date_options.some(d => d.status === 'liste_attente' || d.status === 'complet') && (
                        <a href={`https://wa.me/33660416218?text=${encodeURIComponent(`Bonjour, je souhaite m'inscrire sur la LISTE D'ATTENTE pour "${trip.title}" — uniquement pour la date : ${trip.date_options.filter(d => d.status === 'liste_attente' || d.status === 'complet').map(d => d.label).join(', ')}.\n\n(Note : la date du ${trip.date_options.filter(d => d.status === 'disponible').map(d => d.label).join(', ')} est encore disponible, inscription en ligne sur le site.)\n\nNom : \nPrénom : \nNombre de places : `)}`}
                          target="_blank" rel="noopener noreferrer"
                          className="mt-2 flex items-center justify-center gap-2 w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-2.5 px-4 rounded-lg transition-colors shadow-sm"
                          data-testid="date-options-whatsapp-btn">
                          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                          Liste d'attente — {trip.date_options.filter(d => d.status === 'liste_attente' || d.status === 'complet').map(d => d.label).join(', ')} uniquement
                        </a>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gradient-to-r from-[#5BC5D4] to-[#3BA4B3] text-white rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                        </svg>
                        <span className="font-semibold text-sm opacity-90">Dates du voyage</span>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <div className="text-xs opacity-75">Départ</div>
                          <div className="text-xl font-bold">{formatDate(trip.start_date)}</div>
                        </div>
                        {trip.start_date !== trip.end_date && (
                          <>
                            <div className="text-center text-2xl font-bold">&darr;</div>
                            <div>
                              <div className="text-xs opacity-75">Retour</div>
                              <div className="text-xl font-bold">{formatDate(trip.end_date)}</div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between py-2 border-b">
                    <span className="text-gray-600">Durée</span>
                    <span className="font-semibold">
                      {trip.weekly_pricing && trip.weekly_pricing.length > 0 && !estCourtSejour(trip) ? '7 nuits / semaine' : estCourtSejour(trip) && trip.weekly_pricing?.length ? `${trip.duration_days || 4} jours / ${(trip.duration_days || 4) - 1} nuits` : `${trip.duration_days} ${trip.duration_days === 1 ? 'jour' : 'jours'}`}
                    </span>
                  </div>
                  {/* Weekly availability table for trips with weekly_pricing.
                      Iter 156g — Masquée ici pour Lloret (weekly_pricing) car déjà
                      affichée EN HAUT de la page dans la carte HERO mise en valeur.
                      On garde uniquement le branchement legacy `trip.weekly_availability`
                      sans pricing pour les éventuels autres voyages. */}
                  {trip.weekly_pricing && trip.weekly_pricing.length > 0 ? (
                    <div className="text-xs text-gray-500 italic flex items-center gap-1.5" data-testid="weekly-availability-sidebar-stub">
                      <span>📅</span>
                      <button
                        type="button"
                        onClick={() => {
                          const target = document.querySelector('[data-testid="weekly-availability-hero"]');
                          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="underline hover:text-[#1E3A5F]"
                        data-testid="weekly-availability-scroll-top-btn"
                      >
                        Voir les disponibilités par {motSejour(trip)} ↑
                      </button>
                    </div>
                  ) : (trip.weekly_availability && Object.keys(trip.weekly_availability).length) ? (
                    <div data-testid="weekly-availability-table">
                      <div className="text-sm font-semibold text-[#1E3A5F] mb-2">Disponibilités par {motSejour(trip)}</div>
                      <div className="border border-gray-200 rounded-lg overflow-hidden text-xs">
                        {Object.entries(trip.weekly_availability).sort().map(([key, spots], i) => (
                          <div key={key} className={`flex items-center justify-between px-3 py-1.5 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                            <span className="text-gray-700 text-xs">{key}</span>
                            <span className={`font-bold text-xs ${spots <= 0 ? 'text-orange-500' : spots <= 5 ? 'text-orange-500' : 'text-green-600'}`}>
                              {spots <= 0 ? 'Liste att.' : `${spots} pl.`}
                            </span>
                          </div>
                        ))}
                      </div>
                      {trip.spots_updated_at && (
                        <div className="text-[10px] text-gray-400 text-right mt-1" data-testid="spots-updated-date">
                          Mis à jour le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(trip.spots_updated_at))}
                        </div>
                      )}
                    </div>
                  ) : !trip.date_options ? (
                    <>
                      <div className="flex items-center justify-between py-2 border-b">
                        <span className="text-gray-600">Places restantes</span>
                        <span className={`font-semibold ${dispoStatut === 'complet' ? 'text-orange-500' : dispoStatut === 'moins_10' ? 'text-red-500' : 'text-green-500'}`} data-testid="trip-spots">
                          {dispoStatut === 'complet' ? 'Complet — Liste d\'attente' : dispoStatut === 'moins_10' ? ((trip.dispo_auto?.restantes ?? trip.available_spots) > 0 ? `Plus que ${trip.dispo_auto?.restantes ?? trip.available_spots} place${(trip.dispo_auto?.restantes ?? trip.available_spots) > 1 ? 's' : ''} !` : 'Plus que quelques places !') : 'Places disponibles'}
                        </span>
                      </div>
                      {trip.dispo_auto ? (
                        <div className="text-xs text-gray-400 text-right" data-testid="spots-auto-note">
                          Mis à jour automatiquement en temps réel
                        </div>
                      ) : trip.spots_updated_at && (
                        <div className="text-xs text-gray-400 text-right" data-testid="spots-updated-date">
                          Mis à jour le {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(trip.spots_updated_at))}
                        </div>
                      )}
                    </>
                  ) : null}
                </div>

                {/* Quick links: échéancier + assurance (ouverture en modale in-page) */}
                <div className="space-y-2 mt-4 mb-2" data-testid="sidebar-quick-links">
                  <button type="button" onClick={() => openInfo('echeancier', 'Échéancier de paiement')} className="flex items-center gap-2 text-sm text-[#5BC5D4] hover:text-[#1E3A5F] transition font-medium" data-testid="sidebar-echeancier-btn">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/></svg>
                    Échéancier de paiement
                  </button>
                  <button type="button" onClick={() => openInfo('assurance', 'Assurance multirisque voyage')} className="flex items-center gap-2 text-sm text-[#5BC5D4] hover:text-[#1E3A5F] transition font-medium" data-testid="sidebar-assurance-btn">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/></svg>
                    Assurance voyage
                  </button>
                </div>

                {/* Iter 156f — Pour les voyages multi-semaines (Lloret été), on
                    affiche TOUJOURS la branche "réservez votre place" (jamais le
                    bandeau rouge global), car chaque semaine est gérée individuellement
                    dans le tableau ci-dessus via le badge ⏰ Liste d'attente. */}
                {(trip.weekly_pricing && trip.weekly_pricing.length > 0) || isTripAvailable ? (
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-center mb-2 text-[#1E3A5F]">Réservez votre place</h3>
                    <p className="text-center text-gray-400 italic text-sm mb-6">Le programme est beau, mais l'ambiance compte tout autant.</p>
                    
                    <div className="bg-gradient-to-br from-[#5BC5D4] to-[#4AB4C3] rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-white rounded-full p-3">
                          <svg className="w-6 h-6 text-[#5BC5D4]" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                          </svg>
                        </div>
                        <div className="text-white">
                          <h4 className="font-bold text-xl">Inscription en ligne</h4>
                          <p className="text-sm text-white text-opacity-90">Rapide et sécurisé</p>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <a
                          href="/inscription"
                          data-testid="register-form-btn"
                          className="block w-full bg-white text-[#5BC5D4] px-6 py-3 rounded-xl font-bold hover:bg-gray-50 transition-all text-center shadow-md"
                        >
                          Remplir le formulaire
                        </a>
                        <a
                          href="/moi?payer=1"
                          data-testid="pay-cb-btn"
                          className="block w-full bg-white bg-opacity-20 backdrop-blur text-white px-6 py-3 rounded-xl font-bold hover:bg-opacity-30 transition-all text-center"
                        >
                          Régler (espace adhérent)
                        </a>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#79c9ea] to-[#5ba8c8] rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-white rounded-full p-3">
                          <svg className="w-6 h-6 text-[#79c9ea]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"/>
                          </svg>
                        </div>
                        <div className="text-white">
                          <h4 className="font-bold text-xl">Une question ?</h4>
                          <p className="text-sm text-white text-opacity-90">L'équipe vous répond sur WhatsApp</p>
                        </div>
                      </div>
                      <a
                        href="https://wa.me/message/XTYPH2HUY3L6L1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="appointment-btn"
                        className="block w-full bg-white text-[#79c9ea] px-6 py-3 rounded-xl font-bold hover:bg-gray-50 transition-all text-center shadow-md"
                      >
                        💬 Écrire sur WhatsApp
                      </a>
                    </div>

                    <div className="text-center pt-3 border-t">
                      <p className="text-sm text-gray-600 mb-2">Des questions ?</p>
                      <a
                        href="https://wa.me/message/XTYPH2HUY3L6L1"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="whatsapp-link"
                        className="inline-flex items-center gap-2 text-[#25D366] hover:underline"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                        </svg>
                        Contactez-nous sur WhatsApp
                      </a>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4" id="waitlist-anchor" data-testid="waiting-list-section">
                    <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 text-center">
                      <p className="text-red-600 font-bold text-lg">Voyage complet</p>
                      <p className="text-sm text-gray-600 mt-1">Ce voyage affiche complet, mais des places peuvent se libérer !</p>
                      {trip.spots_updated_at && (
                        <p className="text-xs text-gray-400 mt-1">
                          au {new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(trip.spots_updated_at))}
                        </p>
                      )}
                    </div>
                    <WaitlistForm tripTitle={trip.title} tripId={trip.id} startDate={trip.start_date} />
                  </div>
                )}

                {/* Iter 149 — Solo Matching : visible uniquement pour voyages AVEC hébergement
                    (duration_days > 1). Pas de chambre = pas de partage = pas de bloc. */}
                {trip.duration_days > 1 && (
                  <div className="mt-6">
                    <SoloMatchingForm tripTitle={trip.title} tripId={trip.id} />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Lien Informations administratives */}
          <div className="container-custom mt-8">
            <Link
              to="/informations-administratives"
              onClick={() => window.scrollTo(0, 0)}
              data-testid="info-admin-link"
              className="block w-full bg-gradient-to-r from-[#f0f9ff] to-[#e0f2fe] border border-[#79c9ea]/30 rounded-xl p-4 hover:shadow-md transition-all duration-300 group"
            >
              <div className="flex items-center gap-3">
                <div className="bg-[#79c9ea] rounded-full p-2 group-hover:scale-110 transition-transform">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <span className="font-semibold text-[#1E3A5F] text-sm">Informations administratives</span>
                  <span className="block text-xs text-gray-500">{TP('infos_admin_sous_titre')}</span>
                </div>
                <svg className="w-5 h-5 text-[#79c9ea] ml-auto group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </Link>
          </div>

          {/* Navigation Buttons */}
          <div className="container-custom mt-12 mb-8">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/voyages"
                onClick={() => window.scrollTo(0, 0)}
                data-testid="back-to-trips-btn"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#5BC5D4] to-[#4AB4C3] text-white px-8 py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Tous les voyages
              </Link>
              
              <Link
                to={`/voyages?filter=${trip.transport === 'avion+croisiere' ? 'croisiere' : trip.transport === 'autocar' ? 'car' : trip.transport}`}
                onClick={() => window.scrollTo(0, 0)}
                data-testid="filter-transport-btn"
                className="flex items-center justify-center gap-2 bg-gradient-to-r from-[#79c9ea] to-[#5ba8c8] text-white px-8 py-4 rounded-xl font-bold hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                {trip.transport === 'avion' && 'Autres voyages en avion'}
                {(trip.transport === 'car' || trip.transport === 'autocar') && 'Autres voyages en autocar'}
                {(trip.transport === 'croisiere' || trip.transport === 'avion+croisiere') && 'Autres croisières'}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Iter 156f — Modale liste d'attente par semaine (Lloret été) */}
      <WaitlistWeekModal
        open={waitlistWeekModal.open}
        onClose={() => setWaitlistWeekModal({ open: false, weekKey: '', weekLabel: '', transportLabel: '' })}
        trip={trip}
        weekKey={waitlistWeekModal.weekKey}
        weekLabel={waitlistWeekModal.weekLabel}
        transportLabel={waitlistWeekModal.transportLabel}
      />

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={allImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
        />
      )}

      {/* Modale info (PDF Programme / Échéancier / Assurance) */}
      <InfoModal
        open={infoModal.open}
        onClose={closeInfo}
        type={infoModal.type}
        title={infoModal.title}
        pdfUrl={infoModal.pdfUrl}
      />
      <TripMobileBar trip={trip} />
    </div>
  );
};

export default TripDetail;
